import React from "react";
import { Box, Tooltip } from "@mui/material";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewSidebarRoundedIcon from "@mui/icons-material/ViewSidebarRounded";
import { tokens } from "../../theme";

export type HubView = "list" | "grid" | "detail";

export interface ViewSegmentedProps {
  value: HubView;
  onChange: (v: HubView) => void;
  options?: HubView[];
}

const ICONS: Record<HubView, React.ReactNode> = {
  list: <ViewListRoundedIcon sx={{ fontSize: 22 }} />,
  grid: <GridViewRoundedIcon sx={{ fontSize: 22 }} />,
  detail: <ViewSidebarRoundedIcon sx={{ fontSize: 22 }} />,
};

const LABELS: Record<HubView, string> = {
  list: "List",
  grid: "Grid",
  detail: "Detail",
};

/**
 * Segmented List / Grid / Detail control. Mirrors `.viewseg` from ommuse-theme.css.
 */
export const ViewSegmented: React.FC<ViewSegmentedProps> = ({
  value,
  onChange,
  options = ["list", "grid", "detail"],
}) => (
  <Box
    sx={{
      display: "inline-flex",
      background: tokens.color.surface.raised,
      border: `1px solid ${tokens.color.border}`,
      borderRadius: `${tokens.radius.sm}px`,
      padding: "4px",
      gap: "4px",
      boxShadow: tokens.shadow.edgeHi,
    }}
  >
    {options.map((opt) => {
      const active = opt === value;
      return (
        <Tooltip key={opt} title={LABELS[opt]}>
          <Box
            component="button"
            type="button"
            aria-label={LABELS[opt]}
            aria-pressed={active}
            onClick={() => onChange(opt)}
            sx={{
              display: "grid",
              placeItems: "center",
              width: 42,
              height: 38,
              border: active ? "none" : `1px solid ${tokens.color.border}`,
              borderRadius: `${tokens.radius.sm - 3}px`,
              cursor: "pointer",
              color: active
                ? tokens.color.text.heading
                : tokens.color.text.secondary,
              background: active
                ? tokens.color.accentSoft
                : tokens.color.surface.raised2,
              boxShadow: active
                ? `inset 0 0 0 1px ${tokens.color.accent}40`
                : "none",
              transition: "background .15s, color .15s, border-color .15s",
              "&:hover": {
                color: tokens.color.text.body,
                background: active
                  ? tokens.color.accentSoft
                  : tokens.color.surface.raisedHi,
              },
            }}
          >
            {ICONS[opt]}
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

export default ViewSegmented;
